/**
 * Date Formatting Utilities
 *
 * Centralized date formatting functions used across the application
 */
/**
 * Format a date to a localized date string
 * @param date - Date object, string, or undefined
 * @param locale - Locale string (default: 'en-US')
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string or 'Never' if date is undefined
 */
export function formatDate(date, locale = 'en-US', options) {
    if (!date)
        return 'Never';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    // Check if date is valid
    if (isNaN(dateObj.getTime()))
        return 'Invalid Date';
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...options,
    };
    return dateObj.toLocaleDateString(locale, defaultOptions);
}
/**
 * Format a date with time
 * @param date - Date object, string, or undefined
 * @param locale - Locale string (default: 'en-US')
 * @returns Formatted date and time string
 */
export function formatDateTime(date, locale = 'en-US') {
    if (!date)
        return 'Never';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime()))
        return 'Invalid Date';
    return dateObj.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
/**
 * Format a date to ISO string (YYYY-MM-DD) in LOCAL timezone
 * Useful for date inputs
 *
 * IMPORTANT: This function uses local timezone, NOT UTC.
 * Using toISOString() converts to UTC which can shift the date by one day
 * in certain timezones (e.g., IST, JST where UTC midnight is the previous day).
 */
export function formatDateToISO(date) {
    if (!date)
        return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime()))
        return '';
    // Use local date parts instead of toISOString() which converts to UTC
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Format a Date object to YYYY-MM-DD string in LOCAL timezone
 * This is a convenience function for use in onChange handlers
 *
 * @example
 * // In a date picker onChange:
 * onChange={(date) => {
 *   const dateString = formatDateToLocal(date);
 *   setFormData(prev => ({ ...prev, myDate: dateString }));
 * }}
 */
export function formatDateToLocal(date) {
    if (!date)
        return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Get relative time string (e.g., "2 days ago", "just now")
 */
export function getRelativeTime(date) {
    if (!date)
        return 'Never';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime()))
        return 'Invalid Date';
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffSecs < 60)
        return 'just now';
    if (diffMins < 60)
        return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30)
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(dateObj);
}
//# sourceMappingURL=date-format.js.map